import { useCallback, useEffect, useState } from "react";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { notifyEmail } from "./EmailNotifications";
import baseUrl from "../../../api/baseUrl";
import { PanelCard, SectionHeader } from "../components/accountUi";

type EmailOverview = {
  platform: { configured: boolean; error: string | null };
  sender: { own: boolean; email: string | null; name: string };
  settings?: {
    enabled: boolean;
    credits?: number;
    lowBalance?: boolean;
    noCreditsMessage?: string;
  };
};

function errorMessageFrom(error: any, fallback: string) {
  return error?.response?.data?.message || fallback;
}

export default function EmailTestPanel({ onChanged }: { onChanged?: () => void } = {}) {
  const [overview, setOverview] = useState<EmailOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [testEmail, setTestEmail] = useState("");
  const [message, setMessage] = useState("");
  const [templates, setTemplates] = useState<{ id: string; name: string }[]>([]);
  const [templateId, setTemplateId] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  const loadOverview = useCallback(() => {
    setLoading(true);
    setError(null);
    return baseUrl
      .get("/client/account/email-overview")
      .then((res) => {
        if (res?.data?.success) setOverview(res.data.data);
        else setError("Could not load email details right now.");
      })
      .catch(() => setError("Could not load email details right now."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const loadTemplates = useCallback(() => {
    baseUrl
      .get("/client/account/email-templates")
      .then((res) => {
        const data = res?.data?.data;
        if (data) setTemplates([...(data.custom ?? []), ...(data.automatic ?? [])]);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const sendTest = () => {
    if (!testEmail.trim()) {
      notifyEmail("warning", "Email address needed", "Enter who the email should go to.");
      return;
    }
    setTesting(true);
    setTestResult(null);
    baseUrl
      .post("/client/account/email-test", {
        to: testEmail,
        templateId: templateId || undefined,
        body: templateId ? undefined : message.trim() || undefined,
      })
      .then((res) => {
        setTestResult({ ok: true, message: res?.data?.message || "Email sent" });
        notifyEmail("success", "Email sent!", `Your email was sent to ${testEmail.trim()}.`);
        setMessage("");
      })
      .catch((err) => {
        const errorMessage = errorMessageFrom(err, "Email could not be sent");
        setTestResult({ ok: false, message: errorMessage });
        notifyEmail("error", "Email not sent", errorMessage);
      })
      .finally(() => {
        setTesting(false);
        loadOverview();
        onChanged?.();
      });
  };

  const platform = overview?.platform;
  const credits = overview?.settings?.credits ?? 0;
  const noCredits = Boolean(overview) && credits <= 0;
  const emailOff = Boolean(overview) && !overview?.settings?.enabled;
  const sender = overview?.sender;

  return (
    <PanelCard className="overflow-hidden">
      <div className="border-b border-slate-100 px-5 py-4 dark:border-slate-700/80">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <SectionHeader
            icon={EmailOutlinedIcon}
            title="Email Messages"
            subtitle="Emails are sent through Twilio from your clinic's email sender"
          />
          <button
            type="button"
            onClick={() => loadOverview()}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 self-start rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            <RefreshIcon sx={{ fontSize: 16 }} className={loading ? "animate-spin text-primary" : ""} />
            Refresh email status
          </button>
        </div>
      </div>

      <div className="space-y-4 p-5">
        {error ? (
          <div className="rounded-xl border border-amber-200/80 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-200">
            {error}
          </div>
        ) : null}

        {platform && !platform.configured ? (
          <div className="rounded-xl border border-amber-200/80 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-200">
            {platform.error}
          </div>
        ) : null}

        <div className="grid gap-3 lg:grid-cols-2">
          <PanelCard className="p-4">
            <p className="text-sm font-semibold text-slate-800 dark:text-white">Email sender</p>
            <div className="mt-3 rounded-xl bg-slate-50 px-3 py-2 dark:bg-slate-900/40">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Patients see emails from
              </p>
              <p className="mt-0.5 text-sm font-semibold text-slate-800 dark:text-white">
                {sender?.name || "Your clinic"}
                {sender?.email ? (
                  <span className="ml-1 text-xs font-normal text-slate-500 dark:text-slate-400">
                    ({sender.email})
                  </span>
                ) : null}
              </p>
              {sender && !sender.own ? (
                <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                  Shared Care32 address. To send from your own clinic address, contact Care32
                  support to set up an email sender for your clinic.
                </p>
              ) : null}
            </div>
            {overview ? (
              <p
                className={`mt-3 text-xs ${
                  overview.settings?.enabled
                    ? "text-emerald-700 dark:text-emerald-300"
                    : "text-amber-700 dark:text-amber-300"
                }`}
              >
                {overview.settings?.enabled
                  ? "Email to patients: turned on"
                  : "Email to patients: off. Contact Care32 support to turn it on."}
              </p>
            ) : null}
            {noCredits ? (
              <p className="mt-1 text-xs text-rose-700 dark:text-rose-300">
                {overview?.settings?.noCreditsMessage || "Email tokens are finished. Buy email tokens or contact the Care32 team."}
              </p>
            ) : overview?.settings?.lowBalance ? (
              <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                Email tokens are running low. Buy email tokens to avoid interruptions.
              </p>
            ) : null}
          </PanelCard>

          <PanelCard className="p-4">
            <p className="text-sm font-semibold text-slate-800 dark:text-white">Quick email</p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Send a short message from your clinic to your patients.
            </p>
            {emailOff ? (
              <p className="mt-2 text-xs text-rose-700 dark:text-rose-300">
                Email to patients is turned off for your clinic. Contact the Care32 team to turn it on.
              </p>
            ) : null}
            <div className="mt-3 space-y-2">
              <select
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                onFocus={loadTemplates}
                aria-label="Template"
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
              >
                <option value="">Write my own message</option>
                {templates.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                    {item.id.startsWith("event:") ? " (automatic email)" : ""}
                  </option>
                ))}
              </select>
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="name@example.com"
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
              />
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  type="text"
                  disabled={Boolean(templateId)}
                  value={templateId ? "" : message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={templateId ? "The chosen template is sent" : "Write a short message"}
                  maxLength={500}
                  className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                />
                <button
                  type="button"
                  onClick={sendTest}
                  disabled={testing || !platform?.configured || noCredits || emailOff}
                  className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl border border-primary/30 bg-primary/5 px-4 py-2 text-xs font-semibold text-primary shadow-sm transition hover:bg-primary hover:text-white hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <SendOutlinedIcon sx={{ fontSize: 16 }} />
                  {testing ? "Sending…" : "Send email"}
                </button>
              </div>
            </div>
            {testResult ? (
              <p
                className={`mt-2 text-xs normal-case ${
                  testResult.ok
                    ? "text-emerald-700 dark:text-emerald-300"
                    : "text-rose-700 dark:text-rose-300"
                }`}
              >
                {testResult.message}
              </p>
            ) : null}
          </PanelCard>
        </div>
      </div>
    </PanelCard>
  );
}
